import {
	CURRENT_INTAKE_VERSION,
	mergeWithSectionDefaults,
	type IntakeDraft,
	type IntakeDraftEnvelope,
	IntakeDraftEnvelopeSchema,
	type SectionKey,
} from '#app/routes/mocra+/_form+/intake/intake-draft.ts'

type Listener = () => void

type SetState<T> = T | ((previous: T) => T)

export type IntakeDraftState = {
	readonly version: number
	readonly sections: IntakeDraft
	readonly lastPersistedAt: number | null
	readonly errors: Partial<Record<SectionKey, string>>
}

type PersistenceAdapter = {
	load: () => IntakeDraftEnvelope | null
	save: (snapshot: IntakeDraftEnvelope) => void
	clear?: () => void
}

type CreateIntakeDraftStoreOptions = {
	readonly persistence?: PersistenceAdapter
	readonly onPersistError?: (error: unknown, state: IntakeDraftState) => void
	readonly onPersistSuccess?: (state: IntakeDraftState) => void
}

export type IntakeDraftStore = {
	getState: () => IntakeDraftState
	subscribe: (listener: Listener) => () => void
	updateSection: (
		section: SectionKey,
		value: SetState<unknown>,
	) => IntakeDraftState
	clearSection: (section: SectionKey) => IntakeDraftState
	replaceSections: (sections: IntakeDraft) => IntakeDraftState
	clearAll: () => IntakeDraftState
}

export function createIntakeDraftStore(
	options: CreateIntakeDraftStoreOptions = {},
): IntakeDraftStore {
	const { persistence, onPersistError, onPersistSuccess } = options
	const listeners = new Set<Listener>()
	const initialState = initializeState(persistence)
	let state = initialState

	function getState() {
		return state
	}

	function setState(nextState: IntakeDraftState): IntakeDraftState {
		state = nextState
		for (const listener of listeners) {
			listener()
		}
		return state
	}

	function subscribe(listener: Listener) {
		listeners.add(listener)
		return () => {
			listeners.delete(listener)
		}
	}

	function persistSnapshot(snapshot: IntakeDraftState) {
		if (!persistence) return
		try {
			persistence.save({
				version: snapshot.version,
				sections: snapshot.sections,
			})
			onPersistSuccess?.(snapshot)
		} catch (error) {
			onPersistError?.(error, snapshot)
		}
	}

	function updateSection(
		section: SectionKey,
		value: SetState<unknown>,
	): IntakeDraftState {
		const nextSections = {
			...state.sections,
			[section]:
				typeof value === 'function'
					? (value as (current: unknown) => unknown)(state.sections[section])
					: value,
		}
		const nextState = setState({
			version: state.version,
			sections: nextSections,
			lastPersistedAt: Date.now(),
			errors: state.errors,
		})
		persistSnapshot(nextState)
		return nextState
	}

	function clearSection(section: SectionKey): IntakeDraftState {
		if (!(section in state.sections)) {
			return state
		}
		const { [section]: _removed, ...rest } = state.sections
		const nextState = setState({
			version: state.version,
			sections: rest,
			lastPersistedAt: Date.now(),
			errors: state.errors,
		})
		persistSnapshot(nextState)
		return nextState
	}

	function replaceSections(sections: IntakeDraft): IntakeDraftState {
		const nextState = setState({
			version: CURRENT_INTAKE_VERSION,
			sections,
			lastPersistedAt: Date.now(),
			errors: state.errors,
		})
		persistSnapshot(nextState)
		return nextState
	}

	function clearAll(): IntakeDraftState {
		const clearedState: IntakeDraftState = {
			version: CURRENT_INTAKE_VERSION,
			sections: {},
			lastPersistedAt: Date.now(),
			errors: {},
		}
		setState(clearedState)
		if (persistence?.clear) {
			try {
				persistence.clear()
			} catch (error) {
				onPersistError?.(error, clearedState)
			}
		} else {
			persistSnapshot(clearedState)
		}
		return clearedState
	}

	return {
		getState,
		subscribe,
		updateSection,
		clearSection,
		replaceSections,
		clearAll,
	}
}

function initializeState(persistence?: PersistenceAdapter): IntakeDraftState {
	if (!persistence) {
		return {
			version: CURRENT_INTAKE_VERSION,
			sections: {},
			lastPersistedAt: null,
			errors: {},
		}
	}
	try {
		const snapshot = persistence.load()
		if (!snapshot) {
			return {
				version: CURRENT_INTAKE_VERSION,
				sections: {},
				lastPersistedAt: null,
				errors: {},
			}
		}
		const parsed = IntakeDraftEnvelopeSchema.parse(snapshot)
		return {
			version: Math.max(parsed.version, CURRENT_INTAKE_VERSION),
			sections: mergeWithSectionDefaults(parsed.sections, parsed.version),
			lastPersistedAt: null,
			errors: {},
		}
	} catch (error) {
		console.warn('Failed to load MoCRA intake draft snapshot.', error)
		return {
			version: CURRENT_INTAKE_VERSION,
			sections: {},
			lastPersistedAt: null,
			errors: {},
		}
	}
}
